import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fastapi } from '../../services/api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faArrowLeft, 
  faDollarSign, 
  faFileInvoice, 
  faUsers, 
  faFileInvoiceDollar,
  faEye,
  faTimes,
  faTags,
  faEnvelope,
  faPhone
} from '@fortawesome/free-solid-svg-icons';
import BtnTaratic from '../../components/ui/BtnTaratic';

interface BillingSummary {
  total_revenue: number;
  total_drafts: number;
  total_patients: number;
  total_invoices: number;
}

interface InvoiceLine {
  id: number;
  concept_name: string;
  quantity: number;
  unit_price: number;
  tax_rate: number;
  total: number;
}

interface InvoiceDetail {
  id: number;
  invoice_number: string | null;
  patient_id: number;
  patient_name: string | null;
  issue_date: string;
  type: string;
  status: string;
  subtotal: number;
  total: number;
  lines?: InvoiceLine[]; // Only present in detail view
}

interface PatientAnalysis {
  patient_id: number;
  full_name: string;
  email: string | null;
  phone: string | null;
  total_spent: number;
  invoice_count: number;
  tags: string[];
}

const AdminComercioBillingPage: React.FC = () => {
  const { comercioKey } = useParams<{ comercioKey: string }>();
  const navigate = useNavigate();

  const [summary, setSummary] = useState<BillingSummary | null>(null);
  const [invoices, setInvoices] = useState<InvoiceDetail[]>([]);
  const [patients, setPatients] = useState<PatientAnalysis[]>([]);
  
  const [selectedInvoice, setSelectedInvoice] = useState<InvoiceDetail | null>(null);
  
  const [activeTab, setActiveTab] = useState<'invoices' | 'patients'>('invoices');

  useEffect(() => {
    if (comercioKey) {
      fetchData();
    }
  }, [comercioKey]);

  const fetchData = async () => {
    try {
      const [summaryRes, invoicesRes, patientsRes] = await Promise.all([
        fastapi.get(`/admin/comercios/${comercioKey}/billing/summary`),
        fastapi.get(`/admin/comercios/${comercioKey}/billing/invoices`),
        fastapi.get(`/admin/comercios/${comercioKey}/billing/patients`)
      ]);
      setSummary(summaryRes.data);
      setInvoices(invoicesRes.data);
      setPatients(patientsRes.data);
    } catch (e) {
      console.error('Error fetching billing data', e);
    }
  };

  const openInvoiceDetail = async (invoiceId: number) => {
    try {
      const res = await fastapi.get(`/admin/comercios/${comercioKey}/billing/invoices/${invoiceId}`);
      setSelectedInvoice(res.data);
    } catch (e) {
      console.error('Error fetching invoice details', e);
    }
  };

  const closeInvoiceDetail = () => {
    setSelectedInvoice(null);
  };

  return (
    <div className="p-8 space-y-8 animate-fade-in-up max-w-7xl mx-auto">
      <header className="flex items-center gap-6">
        <BtnTaratic color="ghost-dark" onClick={() => navigate('/admin/comercios')} className="px-4 py-2">
          <FontAwesomeIcon icon={faArrowLeft} />
        </BtnTaratic>
        <div>
          <h1 className="text-4xl font-black text-white">
            Billing Analysis
          </h1>
          <p className="text-white/50 mt-1 font-mono">Tenant: {comercioKey}</p>
        </div>
      </header>

      {/* Dashboard Summary */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-white/5 border border-white/10 p-6 rounded-3xl backdrop-blur-xl relative overflow-hidden group">
            <div className="absolute top-0 right-0 p-6 opacity-20 group-hover:opacity-40 transition-opacity">
              <FontAwesomeIcon icon={faDollarSign} className="text-6xl text-green-400" />
            </div>
            <p className="text-white/50 text-sm uppercase tracking-wider mb-2">Total Revenue</p>
            <p className="text-3xl font-bold text-white">${summary.total_revenue.toFixed(2)}</p>
          </div>
          
          <div className="bg-white/5 border border-white/10 p-6 rounded-3xl backdrop-blur-xl relative overflow-hidden group">
            <div className="absolute top-0 right-0 p-6 opacity-20 group-hover:opacity-40 transition-opacity">
              <FontAwesomeIcon icon={faFileInvoice} className="text-6xl text-pink-400" />
            </div>
            <p className="text-white/50 text-sm uppercase tracking-wider mb-2">Drafts Value</p>
            <p className="text-3xl font-bold text-white">${summary.total_drafts.toFixed(2)}</p>
          </div>

          <div className="bg-white/5 border border-white/10 p-6 rounded-3xl backdrop-blur-xl relative overflow-hidden group">
            <div className="absolute top-0 right-0 p-6 opacity-20 group-hover:opacity-40 transition-opacity">
              <FontAwesomeIcon icon={faUsers} className="text-6xl text-blue-400" />
            </div>
            <p className="text-white/50 text-sm uppercase tracking-wider mb-2">Patients Billed</p>
            <p className="text-3xl font-bold text-white">{summary.total_patients}</p>
          </div>

          <div className="bg-white/5 border border-white/10 p-6 rounded-3xl backdrop-blur-xl relative overflow-hidden group">
            <div className="absolute top-0 right-0 p-6 opacity-20 group-hover:opacity-40 transition-opacity">
              <FontAwesomeIcon icon={faFileInvoiceDollar} className="text-6xl text-purple-400" />
            </div>
            <p className="text-white/50 text-sm uppercase tracking-wider mb-2">Total Invoices</p>
            <p className="text-3xl font-bold text-white">{summary.total_invoices}</p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex items-center gap-4 border-b border-white/10 pb-4 mb-6">
        <BtnTaratic 
          color={activeTab === 'invoices' ? 'pink' : 'ghost-dark'} 
          onClick={() => setActiveTab('invoices')}
        >
          <FontAwesomeIcon icon={faFileInvoiceDollar} className="mr-2" /> Invoices History
        </BtnTaratic>
        <BtnTaratic 
          color={activeTab === 'patients' ? 'blue' : 'ghost-dark'} 
          onClick={() => setActiveTab('patients')}
        >
          <FontAwesomeIcon icon={faUsers} className="mr-2" /> Patient Analysis
        </BtnTaratic>
      </div>

      <div className="animate-fade-in-up">
        {/* Patient Analysis Section */}
        {activeTab === 'patients' && (
          <div className="bg-white/5 border border-white/10 rounded-3xl backdrop-blur-xl flex flex-col max-h-[800px]">
            <div className="p-6 border-b border-white/10">
              <h2 className="text-xl font-bold text-white">Top Spenders (Patients)</h2>
              <p className="text-sm text-white/50">Analysis by total invoiced amount</p>
            </div>
            <div className="overflow-y-auto p-4 space-y-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              {patients.map((p) => (
                <div key={p.patient_id} className="bg-white/5 rounded-2xl p-4 border border-white/5 flex items-center justify-between hover:bg-white/10 transition-colors">
                  <div>
                    <p className="font-semibold text-white">{p.full_name}</p>
                    <div className="flex items-center gap-3 mt-1 text-xs text-white/50">
                      {p.email && <span className="flex items-center gap-1"><FontAwesomeIcon icon={faEnvelope} /> {p.email}</span>}
                      {p.phone && <span className="flex items-center gap-1"><FontAwesomeIcon icon={faPhone} /> {p.phone}</span>}
                    </div>
                    {p.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        <FontAwesomeIcon icon={faTags} className="text-white/30 text-xs mr-1" />
                        {p.tags.map(t => (
                          <span key={t} className="px-2 py-0.5 rounded bg-white/10 text-white/70 text-[10px] uppercase tracking-wider">
                            {t}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="text-xl font-bold text-pink-400">${p.total_spent.toFixed(2)}</p>
                    <p className="text-xs text-white/40">{p.invoice_count} invoices</p>
                  </div>
                </div>
              ))}
              {patients.length === 0 && <p className="text-white/50 col-span-full text-center py-8">No patient data available.</p>}
            </div>
          </div>
        )}

        {/* Invoices List Section */}
        {activeTab === 'invoices' && (
          <div className="bg-white/5 border border-white/10 rounded-3xl backdrop-blur-xl flex flex-col max-h-[800px]">
            <div className="p-6 border-b border-white/10">
              <h2 className="text-xl font-bold text-white">Invoices History</h2>
              <p className="text-sm text-white/50">All drafts and issued invoices</p>
            </div>
            <div className="overflow-y-auto">
              <table className="w-full text-left text-sm text-white/80 whitespace-nowrap">
                <thead className="bg-white/5 text-white/60 uppercase font-semibold border-b border-white/10 sticky top-0 backdrop-blur-md">
                  <tr>
                    <th className="px-6 py-4">Number</th>
                    <th className="px-6 py-4">Patient</th>
                    <th className="px-6 py-4 text-center">Status</th>
                    <th className="px-6 py-4 text-right">Total</th>
                    <th className="px-6 py-4 text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {invoices.map((inv) => (
                    <tr key={inv.id} className="hover:bg-white/5 transition-colors">
                      <td className="px-6 py-4 font-mono text-xs">{inv.invoice_number || 'N/A'}</td>
                      <td className="px-6 py-4">{inv.patient_name}</td>
                      <td className="px-6 py-4 text-center">
                        <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                          inv.status === 'BORRADOR' 
                            ? 'bg-yellow-500/10 text-yellow-400 border border-yellow-500/20' 
                            : 'bg-green-500/10 text-green-400 border border-green-500/20'
                        }`}>
                          {inv.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right font-bold">${inv.total.toFixed(2)}</td>
                      <td className="px-6 py-4 text-right">
                        <BtnTaratic color="ghost" onClick={() => openInvoiceDetail(inv.id)} className="!px-3 !py-1 text-xs">
                          <FontAwesomeIcon icon={faEye} /> View
                        </BtnTaratic>
                      </td>
                    </tr>
                  ))}
                  {invoices.length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-6 py-12 text-center text-white/50">
                        No invoices found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Invoice Detail Modal */}
      {selectedInvoice && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={closeInvoiceDetail}></div>
          <div className="relative bg-[#111] border border-white/10 p-8 rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col animate-fade-in-up">
            <button 
              onClick={closeInvoiceDetail}
              className="absolute top-6 right-6 text-white/50 hover:text-white transition-colors"
            >
              <FontAwesomeIcon icon={faTimes} className="text-xl" />
            </button>
            
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-white mb-1 flex items-center gap-3">
                <FontAwesomeIcon icon={faFileInvoiceDollar} className="text-pink-400" />
                Invoice Detail
              </h2>
              <p className="text-white/50 font-mono text-sm">{selectedInvoice.invoice_number || `Draft #${selectedInvoice.id}`}</p>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="p-4 bg-white/5 rounded-2xl border border-white/5">
                <p className="text-xs text-white/50 uppercase tracking-wider mb-1">Patient</p>
                <p className="font-semibold text-white">{selectedInvoice.patient_name}</p>
              </div>
              <div className="p-4 bg-white/5 rounded-2xl border border-white/5">
                <p className="text-xs text-white/50 uppercase tracking-wider mb-1">Issue Date</p>
                <p className="font-semibold text-white">{selectedInvoice.issue_date || 'N/A'}</p>
              </div>
              <div className="p-4 bg-white/5 rounded-2xl border border-white/5">
                <p className="text-xs text-white/50 uppercase tracking-wider mb-1">Type</p>
                <p className="font-semibold text-white">{selectedInvoice.type}</p>
              </div>
              <div className="p-4 bg-white/5 rounded-2xl border border-white/5">
                <p className="text-xs text-white/50 uppercase tracking-wider mb-1">Status</p>
                <span className={`inline-block px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider mt-1 ${
                  selectedInvoice.status === 'BORRADOR' 
                    ? 'bg-yellow-500/10 text-yellow-400 border border-yellow-500/20' 
                    : 'bg-green-500/10 text-green-400 border border-green-500/20'
                }`}>
                  {selectedInvoice.status}
                </span>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto min-h-[200px] border border-white/10 rounded-2xl bg-black/20 p-4">
              <table className="w-full text-left text-sm text-white/80">
                <thead className="text-white/50 text-xs uppercase tracking-wider border-b border-white/10">
                  <tr>
                    <th className="pb-3 font-medium">Concept</th>
                    <th className="pb-3 font-medium text-right">Qty</th>
                    <th className="pb-3 font-medium text-right">Price</th>
                    <th className="pb-3 font-medium text-right">Tax</th>
                    <th className="pb-3 font-medium text-right">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {selectedInvoice.lines?.map(line => (
                    <tr key={line.id}>
                      <td className="py-3 text-white">{line.concept_name}</td>
                      <td className="py-3 text-right">{line.quantity}</td>
                      <td className="py-3 text-right">${line.unit_price.toFixed(2)}</td>
                      <td className="py-3 text-right">{line.tax_rate}%</td>
                      <td className="py-3 text-right font-semibold">${line.total.toFixed(2)}</td>
                    </tr>
                  ))}
                  {(!selectedInvoice.lines || selectedInvoice.lines.length === 0) && (
                    <tr>
                      <td colSpan={5} className="py-8 text-center text-white/30">No items found for this invoice.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="mt-6 flex justify-end">
              <div className="w-64 space-y-2 text-sm">
                <div className="flex justify-between text-white/60">
                  <span>Subtotal</span>
                  <span>${selectedInvoice.subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between font-bold text-lg text-white pt-2 border-t border-white/10">
                  <span>Total</span>
                  <span className="text-pink-400">${selectedInvoice.total.toFixed(2)}</span>
                </div>
              </div>
            </div>

          </div>
        </div>
      )}
    </div>
  );
};

export default AdminComercioBillingPage;
